/**
 * Error logging
 *
 * Can be used to log errors to console or and eternal
 * error logging system, like Sentry for example.
 *
 */

import * as Sentry from '@sentry/browser';
import appSettings from '../config/settings';

const ingoreErrorsMap = {
  ['ResizeObserver loop limit exceeded']: true, // Some exotic browsers seems to emit these.
  ['Error reading']: true, // Ignore file reader errors (ImageFromFile)
  ['AxiosError: Network Error']: true,
};

const pickSelectedErrors = (ignored, entry) => {
  const [key, value] = entry;
  return value === true ? [...ignored, key] : ignored;
};

/**
 * Set up error handling. If a Sentry DSN is
 * provided a Sentry client will be installed.
 */
export const setup = () => {
  if (appSettings.sentryDsn) {
    const ignoreErrors = Object.entries(ingoreErrorsMap).reduce(pickSelectedErrors, []);

    // Configures the Sentry client. Adds a handler for
    // any uncaught exception.
    Sentry.init({
      dsn: appSettings.sentryDsn,
      environment: appSettings.env,
      ignoreErrors,
    });
  }
};

/**
 * Set user ID for the logger so that it
 * can be attached to Sentry issues.
 *
 * @param {String} userId ID of current user
 */
export const setUserId = userId => {
  Sentry.setUser({ id: userId });
};

/**
 * Clears the user ID.
 */

export const clearUserId = () => {
  Sentry.setUser(null);
};

const printAPIErrorsAsConsoleTable = apiErrors => {
  if (apiErrors != null && apiErrors.length > 0 && typeof console.table === 'function') {
    console.log('Errors returned by Marketplace API call:');
    console.table(apiErrors.map(err => ({ status: err.status, code: err.code, ...err.meta })));
  }
};

const responseAPIErrors = error => {
  return error && error.data && error.data.errors ? error.data.errors : [];
};

const responseApiErrorInfo = err =>
  responseAPIErrors(err).map(e => ({
    status: e.status,
    code: e.code,
    meta: e.meta,
  }));

/**
 * Logs an exception. If Sentry is configured
 * sends the error information there. Otherwise
 * prints the error to the console.
 *
 * @param {Error} e Error that occurred
 * @param {String} code Error code
 * @param {Object} data Additional data to be sent to Sentry
 */
export const error = (e, code, data) => {
  const apiErrors = responseApiErrorInfo(e);
  
  // Always log a simplified version to console
  const errorMessage = e?.message || e?.toString() || 'Unknown error';
  console.error(`[${code}] ${errorMessage}`);
  
  if (data && Object.keys(data).length > 0) {
    // Log simplified data (exclude full componentStack if present)
    const simplifiedData = { ...data };
    if (simplifiedData.componentStack && simplifiedData.fullComponentStack) {
      // Only show simplified stack in console
      delete simplifiedData.fullComponentStack;
    }
    if (Object.keys(simplifiedData).length > 0) {
      console.error('Error data:', simplifiedData);
    }
  }
  
  if (appSettings.sentryDsn) {
    const extra = { ...data, apiErrorData: apiErrors };

    Sentry.withScope(scope => {
      scope.setTag('code', code);
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureException(e);
    });

    printAPIErrorsAsConsoleTable(apiErrors);
  } else {
    // Only log full error if Sentry is not configured
    if (e && e.stack) {
      console.error('Full error:', e);
    }
    printAPIErrorsAsConsoleTable(apiErrors);
  }
};

const setCause = (error, cause) => {
  const seenErrors = new WeakSet();

  const setCauseIfNoExistingCause = (error, cause) => {
    if (seenErrors.has(error)) {
      return;
    }
    if (error.cause) {
      seenErrors.add(error);
      return setCauseIfNoExistingCause(error.cause, cause);
    }
    error.cause = cause;
  };

  setCauseIfNoExistingCause(error, cause);
};

export const onRecoverableError = (e, componentStack) => {
  let data = {};

  if (componentStack) {
    // Generating this synthetic error allows monitoring services to apply sourcemaps
    // to unminify the stacktrace and make it readable.
    const errorBoundaryError = new Error(e?.message || String(e));
    errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name || 'Error'}`;
    if (typeof componentStack === 'string') {
      errorBoundaryError.stack = componentStack;
    }

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause
    setCause(e, errorBoundaryError);

    // Extract a simplified component stack for better readability
    if (typeof componentStack === 'string') {
      const simplifiedStack = componentStack
        .split('\n')
        .slice(0, 10) // Only keep first 10 lines
        .map(line => {
          // Extract component names from stack trace
          const match = line.match(/at\s+(\w+)/);
          return match ? match[1] : line.trim();
        })
        .filter(line => line && !line.includes('react-dom') && !line.includes('scheduler'))
        .join(' -> ');
      
      data.componentStack = simplifiedStack || componentStack.substring(0, 500);
      data.fullComponentStack = componentStack; // Keep full stack for Sentry
    } else {
      data.componentStack = String(componentStack).substring(0, 500);
      data.fullComponentStack = componentStack;
    }
  }

  // Log a simplified error message to console
  console.error('[React Error]', e?.message || String(e));
  if (data.componentStack) {
    console.error('[Component Stack]', data.componentStack);
  }

  // Replace with your error monitoring service.
  error(e, 'recoverable-error', data);
};
